import { useForm } from "@/src/provider/Form/FormProvider";
import { translate } from "@/src/services/translations/translations";
import Grid from "@mui/material/Grid";
import Group from "@/src/components/Wizard/Group";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import IconEnforce from "@/src/assets/icons/contract/toggleable/IconEnforce";
import IconCompensation from "@/src/assets/icons/employment/toggleable/IconCompensation";
import wizardStyle from "@/src/components/Form/wizardStyle";
import { requiredValidator } from "@/src/services/validationRules";
import ValidatorMultiSelectFieldWithCheckboxes from "@/src/components/FormFields/ValidatorMultiSelectFieldWithCheckboxes";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import TextFieldConfigured from "@/src/components/Form/ConfiguredFormElements/TextFieldConfigured/TextFieldConfigured";
import DateFieldConfigured from "@/src/components/Form/ConfiguredFormElements/DateFieldConfigured/DateFieldConfigured";
import IconWrittenWarning from "@/src/assets/icons/employment/toggleable/IconWrittenWarning";
import IconCheckContract from "@/src/assets/icons/employment/toggleable/IconCheckContract";
import IconDemand from "@/src/assets/icons/employment/toggleable/IconDemand";
import IconQuestionmark from "@/src/assets/icons/common/toggleable/IconQuestionmark";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import NumberFieldConfigured from "@/src/components/Form/ConfiguredFormElements/NumberFieldConfigured/NumberFieldConfigured";
import React from "react";
import { arrayUtils } from "@/src/services/utils";

type EmploymentContractDetailsDefendClaimProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
};

export default function EmploymentContractDetailsDefendClaim({ product, submitProps }: EmploymentContractDetailsDefendClaimProps) {
  const { values } = useForm();

  const getReasonsForDisputeOptions = () => {
    const options = [];

    if (product.subjectOfDispute === "money" || product.subjectOfDispute === "moneyAndGoods") {
      options.push(
        {
          labelText: translate("employmentContract.reasonsForDispute.options.claimedPayment.label", values),
          labelIcon: <IconEnforce />,
          labelIconActive: <IconEnforce active />,
          optionHintText: translate("employmentContract.reasonsForDispute.options.claimedPayment.optionHintText", values),
          value: "claimedPayment",
        },
        {
          labelText: translate("employmentContract.reasonsForDispute.options.allegedDamage.label", values),
          labelIcon: <IconCompensation />,
          labelIconActive: <IconCompensation active />,
          optionHintText: translate("employmentContract.reasonsForDispute.options.allegedDamage.optionHintText", values),
          value: "allegedDamage",
        }
      );
    }

    if (product.subjectOfDispute !== "money") {
      options.push(
        {
          labelText: translate("employmentContract.reasonsForDispute.options.writtenWarning.label", values),
          labelIcon: <IconWrittenWarning />,
          labelIconActive: <IconWrittenWarning active />,
          optionHintText: translate("employmentContract.reasonsForDispute.options.writtenWarning.optionHintText", values),
          value: "writtenWarning",
        },
        {
          labelText: translate("employmentContract.reasonsForDispute.options.termination.label", values),
          labelIcon: <IconCheckContract />,
          labelIconActive: <IconCheckContract active />,
          optionHintText: translate("employmentContract.reasonsForDispute.options.termination.optionHintText", values),
          value: "termination",
        },
        {
          labelText: translate("employmentContract.reasonsForDispute.options.itemsToReturn.label", values),
          labelIcon: <IconDemand />,
          labelIconActive: <IconDemand active />,
          optionHintText: translate("employmentContract.reasonsForDispute.options.itemsToReturn.optionHintText", values),
          value: "itemsToReturn",
        },
        {
          labelText: translate("employmentContract.reasonsForDispute.options.other.label", values),
          labelIcon: <IconQuestionmark />,
          labelIconActive: <IconQuestionmark active />,
          value: "other",
        }
      );
    }

    return options;
  };

  return (
    <>
      <Grid size={12}>
        <Group
          type={"checkbox"}
          name={"reasonsForDispute"}
          question={translate("employmentContract.reasonsForDispute.question", values)}
          options={getReasonsForDisputeOptions()}
          validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
        />
      </Grid>
      {arrayUtils.getArraysIntersect(values.reasonsForDispute, ["claimedPayment"]) && (
        <>
          <Grid sx={wizardStyle.gridTopSpaced} size={12}>
            <FormLegend>{translate("employmentContract.claimedPaymentTypes.question", values)}</FormLegend>
          </Grid>
          <Grid size={12}>
            <ValidatorMultiSelectFieldWithCheckboxes
              label={translate("employmentContract.claimedPaymentTypes.label", values)}
              name={"claimedPaymentTypes"}
              options={[
                {
                  value: "salary",
                  label: translate("employmentContract.claimedPaymentTypes.options.salary.label", values),
                },
                {
                  value: "bonus",
                  label: translate("employmentContract.claimedPaymentTypes.options.bonus.label", values),
                },
                {
                  value: "training",
                  label: translate("employmentContract.claimedPaymentTypes.options.training.label", values),
                },
                {
                  value: "loan",
                  label: translate("employmentContract.claimedPaymentTypes.options.loan.label", values),
                },
                {
                  value: "other",
                  label: translate("employmentContract.claimedPaymentTypes.options.other.label", values),
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>
          <TextFieldConfigured caseData={values} fieldPath={"claimedPaymentReason"} multiline required />
        </>
      )}
      {arrayUtils.getArraysIntersect(values.reasonsForDispute, ["allegedDamage"]) && (
        <>
          <Grid sx={wizardStyle.gridTopSpaced} size={12}>
            <FormLegend>{translate("employmentContract.allegedDamageTypes.question", values)}</FormLegend>
          </Grid>
          <Grid size={12}>
            <ValidatorMultiSelectFieldWithCheckboxes
              label={translate("employmentContract.allegedDamageTypes.label", values)}
              name={"allegedDamageTypes"}
              options={[
                {
                  value: "damageOrLossOfProperty",
                  label: translate("employmentContract.allegedDamageTypes.options.damageOrLossOfProperty.label", values),
                },
                {
                  value: "breachOfNonCompete",
                  label: translate("employmentContract.allegedDamageTypes.options.breachOfNonCompete.label", values),
                },
                {
                  value: "breachOfConfidentiality",
                  label: translate("employmentContract.allegedDamageTypes.options.breachOfConfidentiality.label", values),
                },
                {
                  value: "poorPerformance",
                  label: translate("employmentContract.allegedDamageTypes.options.poorPerformance.label", values),
                },
                {
                  value: "embezzlementOrTheft",
                  label: translate("employmentContract.allegedDamageTypes.options.embezzlementOrTheft.label", values),
                },
                {
                  value: "other",
                  label: translate("employmentContract.allegedDamageTypes.options.other.label", values),
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>
          <DateFieldConfigured caseData={values} fieldPath={"allegedDamageDate"} required />
        </>
      )}
      {arrayUtils.getArraysIntersect(values.reasonsForDispute, ["writtenWarning"]) && (
        <>
          <Grid sx={[wizardStyle.gridTopSpaced, { paddingBottom: "1rem" }]} size={12}>
            <FormLegend>Um Ihre Abmahnung prüfen zu können, benötigen wir Details zum Erhalt und dem Grund der Abmahnung.</FormLegend>
          </Grid>
          <DateFieldConfigured caseData={values} fieldPath={"writtenWarningReceivedDate"} required />
          <TextFieldConfigured caseData={values} fieldPath={"writtenWarningReason"} required multiline />

          <Grid sx={wizardStyle.gridTopSpaced} size={12}>
            <FormLegend>{translate("employmentContract.writtenWarningIllegalReasons.question", values)}</FormLegend>
          </Grid>
          <Grid size={12}>
            <ValidatorMultiSelectFieldWithCheckboxes
              label={translate("employmentContract.writtenWarningIllegalReasons.label", values)}
              name={"writtenWarningIllegalReasons"}
              options={[
                {
                  value: "factuallyIncorrect",
                  label: translate("employmentContract.writtenWarningIllegalReasons.options.factuallyIncorrect.label", values),
                },
                {
                  value: "disproportionateWarning",
                  label: translate("employmentContract.writtenWarningIllegalReasons.options.disproportionateWarning.label", values),
                },
                {
                  value: "formalErrors",
                  label: translate("employmentContract.writtenWarningIllegalReasons.options.formalErrors.label", values),
                },
                {
                  value: "other",
                  label: translate("employmentContract.writtenWarningIllegalReasons.options.other.label", values),
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>
        </>
      )}
      {arrayUtils.getArraysIntersect(values.reasonsForDispute, ["termination"]) && (
        <>
          <Grid sx={[wizardStyle.gridTopSpaced, { paddingBottom: "1rem" }]} size={12}>
            <FormLegend>
              Um Ihren Aufhebungsvertrag prüfen zu können, benötigen wir einige grundlegende Informationen zum Vertrag und Ihrer Situation.
            </FormLegend>
          </Grid>
          <DateFieldConfigured caseData={values} fieldPath={"terminationAgreementOfferDate"} required />
          <DateFieldConfigured caseData={values} fieldPath={"terminationAgreementDeadlineDate"} required />

          <Grid size={12}>
            <Group
              type={"radio"}
              name={"terminationAgreementIncludesSettlement"}
              question={translate("employmentContract.terminationAgreementIncludesSettlement.question", values)}
              options={[
                {
                  labelText: translate("employmentContract.terminationAgreementIncludesSettlement.options.yes.label", values),
                  labelIcon: <CheckIcon />,
                  labelIconActive: <CheckIcon active />,
                  value: "yes",
                },
                {
                  labelText: translate("employmentContract.terminationAgreementIncludesSettlement.options.no.label", values),
                  labelIcon: <CrossIcon />,
                  labelIconActive: <CrossIcon active />,
                  value: "no",
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>

          {values.terminationAgreementIncludesSettlement === "yes" && (
            <NumberFieldConfigured caseData={values} fieldPath={"offeredSettlementPayment"} fieldType={"money"} required />
          )}

          <Grid sx={wizardStyle.gridTopSpaced} size={12}>
            <FormLegend>{translate("employmentContract.terminationAgreementObjectivesClient.question", values)}</FormLegend>
          </Grid>
          <Grid size={12}>
            <ValidatorMultiSelectFieldWithCheckboxes
              label={translate("employmentContract.terminationAgreementObjectivesClient.label", values)}
              name={"terminationAgreementObjectivesClient"}
              options={[
                {
                  value: "maximizeSettlement",
                  label: translate("employmentContract.terminationAgreementObjectivesClient.options.maximizeSettlement.label", values),
                },
                {
                  value: "avoidUnemploymentPenalty",
                  label: translate("employmentContract.terminationAgreementObjectivesClient.options.avoidUnemploymentPenalty.label", values),
                },
                {
                  value: "securePositiveWorkCertificate",
                  label: translate("employmentContract.terminationAgreementObjectivesClient.options.securePositiveWorkCertificate.label", values),
                },
                {
                  value: "achieveImmediateRelease",
                  label: translate("employmentContract.terminationAgreementObjectivesClient.options.achieveImmediateRelease.label", values),
                },
                {
                  value: "negotiateOtherTerms",
                  label: translate("employmentContract.terminationAgreementObjectivesClient.options.negotiateOtherTerms.label", values),
                },
                {
                  value: "remainEmployed",
                  label: translate("employmentContract.terminationAgreementObjectivesClient.options.remainEmployed.label", values),
                },
                {
                  value: "other",
                  label: translate("employmentContract.terminationAgreementObjectivesClient.options.other.label", values),
                },
              ]}
              validators={[requiredValidator("Mindestens einer Auswahl muss getroffen werden")]}
            />
          </Grid>
        </>
      )}
      {arrayUtils.getArraysIntersect(values.reasonsForDispute, ["itemsToReturn"]) && (
        <>
          <Grid sx={wizardStyle.gridTopSpaced} size={12}>
            <FormLegend>{translate("employmentContract.itemsToReturn.question", values)}</FormLegend>
          </Grid>
          <Grid size={12}>
            <ValidatorMultiSelectFieldWithCheckboxes
              label={translate("employmentContract.itemsToReturn.label", values)}
              name={"itemsToReturn"}
              options={[
                {
                  value: "companyCar",
                  label: translate("employmentContract.itemsToReturn.options.companyCar.label", values),
                },
                {
                  value: "laptopOrTablet",
                  label: translate("employmentContract.itemsToReturn.options.laptopOrTablet.label", values),
                },
                {
                  value: "companyPhone",
                  label: translate("employmentContract.itemsToReturn.options.companyPhone.label", values),
                },
                {
                  value: "keysOrAccessCards",
                  label: translate("employmentContract.itemsToReturn.options.keysOrAccessCards.label", values),
                },
                {
                  value: "toolsOrEquipment",
                  label: translate("employmentContract.itemsToReturn.options.toolsOrEquipment.label", values),
                },
                {
                  value: "companyDocuments",
                  label: translate("employmentContract.itemsToReturn.options.companyDocuments.label", values),
                },
                {
                  value: "other",
                  label: translate("employmentContract.itemsToReturn.options.other.label", values),
                },
              ]}
              validators={[requiredValidator("Mindestens einer Auswahl muss getroffen werden")]}
            />
          </Grid>

          <TextFieldConfigured caseData={values} fieldPath={"itemsToReturnReason"} required multiline />

          <Grid sx={wizardStyle.gridTopSpaced} size={12}>
            <FormLegend>{translate("employmentContract.itemsToReturnRejectionReasons.question", values)}</FormLegend>
          </Grid>
          <Grid size={12}>
            <ValidatorMultiSelectFieldWithCheckboxes
              label={translate("employmentContract.itemsToReturnRejectionReasons.label", values)}
              name={"itemsToReturnRejectionReasons"}
              options={[
                {
                  value: "itemsBelongToMe",
                  label: translate("employmentContract.itemsToReturnRejectionReasons.options.itemsBelongToMe.label", values),
                },
                {
                  value: "itemsNeededForWork",
                  label: translate("employmentContract.itemsToReturnRejectionReasons.options.itemsNeededForWork.label", values),
                },
                {
                  value: "notContractuallyAgreed",
                  label: translate("employmentContract.itemsToReturnRejectionReasons.options.notContractuallyAgreed.label", values),
                },
                {
                  value: "disproportionateClaim",
                  label: translate("employmentContract.itemsToReturnRejectionReasons.options.disproportionateClaim.label", values),
                },
                {
                  value: "claimExpired",
                  label: translate("employmentContract.itemsToReturnRejectionReasons.options.claimExpired.label", values),
                },
                {
                  value: "other",
                  label: translate("employmentContract.itemsToReturnRejectionReasons.options.other.label", values),
                },
              ]}
              validators={[requiredValidator("Mindestens einer Auswahl muss getroffen werden")]}
            />
          </Grid>
        </>
      )}
    </>
  );
}
